import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { SorterResult } from 'antd/es/table/interface';
import { AppButton } from '@/components/atoms';
import { Plus } from '@/components/atoms/AppIcon';
import { AdminClinicsListTemplate, LoadingTemplate } from '@/components/templates';
import { LatlonCachesFilterPanel, LatlonCachesTableSection } from '@/components/organisms';
import { useAdminLatlonCachesList } from '@/hooks/admin/useAdminLatlonCaches';
import { CSV_SUCCESS_MESSAGE, CSV_ERROR_MESSAGE } from '@/constants';
import type { LatlonCacheListParams, LatlonCacheListItem } from '@/services/admin/latlon-caches';
import { downloadLatlonCachesCsv } from '@/services/admin/latlon-caches';
import { getTenantSiteTypes } from '@/services/tenants';
import type { TenantSiteTypeItem } from '@/services/tenants';
import { useSiteAdminAuth } from '@/shared/providers/SiteAdminAuthContext';
import useAppToast from '@/hooks/useAppToast';

const DEFAULT_PER_PAGE = 20;

type FilterState = {
  siteTypes: string[];
  clinicNumber: string;
  address: string;
};

const getFiltersFromSearchParams = (searchParams: URLSearchParams): FilterState => {
  const siteTypesFromArray = searchParams.getAll('site_types').filter(Boolean);
  const siteTypesFromCsv = (searchParams.get('site_types') ?? '')
    .split(',')
    .map(v => v.trim())
    .filter(Boolean);
  const siteTypes = siteTypesFromArray.length > 0 ? siteTypesFromArray : siteTypesFromCsv;

  return {
    siteTypes,
    clinicNumber: searchParams.get('clinic_number') ?? '',
    address: searchParams.get('address') ?? '',
  };
};

const getPageFromSearchParams = (searchParams: URLSearchParams): number => {
  const raw = Number(searchParams.get('page'));
  return Number.isInteger(raw) && raw >= 1 ? raw : 1;
};

const SiteAdminLatlonCachesListPage: React.FC = () => {
  const { site } = useParams<{ site: string }>();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isLoading: isAuthLoading } = useSiteAdminAuth();
  const toast = useAppToast();

  const isSuperAdminRoute = pathname.startsWith('/super-admin/admin');

  const siteTypesQuery = useQuery({
    queryKey: ['tenants', 'site-types', isSuperAdminRoute ? 'all' : (site ?? '')],
    queryFn: () => getTenantSiteTypes(isSuperAdminRoute ? undefined : (site ?? undefined)),
    enabled: !isAuthLoading && !!site,
    staleTime: Infinity,
  });

  const tenantSiteTypeList: TenantSiteTypeItem[] = siteTypesQuery.data?.data?.site_types ?? [];
  const showSiteFilter = tenantSiteTypeList.length > 1;

  const tenantNameBySiteType = useMemo(() => {
    const map = new Map<string, string>();
    tenantSiteTypeList.forEach(t => {
      if (t.site_type) map.set(t.site_type, t.name);
    });
    return map;
  }, [tenantSiteTypeList]);

  const tenantSiteTypes = useMemo(
    () => tenantSiteTypeList.map(t => t.site_type).filter(Boolean) as string[],
    [tenantSiteTypeList]
  );

  useEffect(() => {
    if (siteTypesQuery.isError) {
      toast.error('サイト一覧の取得に失敗しました。');
    }
  }, [siteTypesQuery.isError, toast]);

  const initialFilters = getFiltersFromSearchParams(searchParams);

  const [siteTypes, setSiteTypes] = useState<string[]>(initialFilters.siteTypes);
  const [clinicNumber, setClinicNumber] = useState(initialFilters.clinicNumber);
  const [address, setAddress] = useState(initialFilters.address);

  const [appliedSiteTypes, setAppliedSiteTypes] = useState<string[]>(initialFilters.siteTypes);
  const [appliedClinicNumber, setAppliedClinicNumber] = useState(initialFilters.clinicNumber);
  const [appliedAddress, setAppliedAddress] = useState(initialFilters.address);

  const hasAllSelected =
    tenantSiteTypes.length > 0 && tenantSiteTypes.every(st => siteTypes.includes(st));

  const page = getPageFromSearchParams(searchParams);
  const sortBy = searchParams.get('sort_by') ?? undefined;
  const order = searchParams.get('order') as 'ascend' | 'descend' | null;

  useEffect(() => {
    const nextFilters = getFiltersFromSearchParams(searchParams);
    setSiteTypes(nextFilters.siteTypes);
    setClinicNumber(nextFilters.clinicNumber);
    setAddress(nextFilters.address);
    setAppliedSiteTypes(nextFilters.siteTypes);
    setAppliedClinicNumber(nextFilters.clinicNumber);
    setAppliedAddress(nextFilters.address);
  }, [searchParams]);

  const params: LatlonCacheListParams = useMemo(
    () => ({
      site_type: showSiteFilter && appliedSiteTypes.length > 0 ? appliedSiteTypes : undefined,
      clinic_number: appliedClinicNumber || undefined,
      address: appliedAddress || undefined,
      sort_by: sortBy,
      order: order === 'ascend' ? 'asc' : order === 'descend' ? 'desc' : undefined,
      page,
      per_page: DEFAULT_PER_PAGE,
    }),
    [appliedSiteTypes, appliedClinicNumber, appliedAddress, showSiteFilter, sortBy, order, page]
  );

  const { data, isLoading, isFetching } = useAdminLatlonCachesList(site ?? null, params);

  const innerData = data?.data;
  const rows = innerData?.data ?? [];
  const meta = innerData?.meta
    ? {
        current_page: innerData.meta.current_page,
        last_page: innerData.meta.last_page,
        per_page: innerData.meta.per_page,
        total: innerData.meta.total,
      }
    : { current_page: 1, last_page: 1, per_page: DEFAULT_PER_PAGE, total: 0 };

  const handleSearch = () => {
    const nextParams = new URLSearchParams();
    if (showSiteFilter) {
      siteTypes
        .map(v => v.trim())
        .filter(Boolean)
        .forEach(v => nextParams.append('site_types', v));
    }
    if (clinicNumber.trim()) nextParams.set('clinic_number', clinicNumber.trim());
    if (address.trim()) nextParams.set('address', address.trim());
    nextParams.set('page', '1');
    setSearchParams(nextParams);
  };

  const handleSort = (
    sorter: SorterResult<LatlonCacheListItem> | SorterResult<LatlonCacheListItem>[]
  ) => {
    const single = Array.isArray(sorter) ? sorter[0] : sorter;
    const nextParams = new URLSearchParams(searchParams);
    if (single.order) {
      nextParams.set('sort_by', (single.field || single.columnKey) as string);
      nextParams.set('order', single.order);
    } else {
      nextParams.delete('sort_by');
      nextParams.delete('order');
    }
    nextParams.set('page', '1');
    setSearchParams(nextParams);
  };

  const [csvLoading, setCsvLoading] = useState(false);
  const handleCsv = useCallback(async () => {
    if (!site) return;
    setCsvLoading(true);
    try {
      await downloadLatlonCachesCsv(site, params);
      toast.success(CSV_SUCCESS_MESSAGE);
    } catch {
      toast.error(CSV_ERROR_MESSAGE);
    } finally {
      setCsvLoading(false);
    }
  }, [site, params, toast]);

  const handleChangePage = (nextPage: number) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('page', String(nextPage));
    setSearchParams(nextParams);
  };

  if (isAuthLoading) return <LoadingTemplate />;

  return (
    <AdminClinicsListTemplate
      breadcrumbItems={[{ title: '緯度経度キャッシュ' }]}
      title="緯度経度キャッシュ"
      filter={
        <LatlonCachesFilterPanel
          tenants={tenantSiteTypeList}
          showSiteFilter={showSiteFilter}
          siteTypes={siteTypes}
          hasAllSelected={hasAllSelected}
          onChangeSiteTypes={setSiteTypes}
          clinicNumber={clinicNumber}
          onChangeClinicNumber={setClinicNumber}
          address={address}
          onChangeAddress={setAddress}
          onSearch={handleSearch}
          onCsv={handleCsv}
          csvLoading={csvLoading}
        />
      }
      betweenFilterAndTable={
        <div className="mb-4">
          <Link to={`/${site}/admin/latlon_caches/create`}>
            <AppButton icon={<Plus />} type="primary" className="py-1 my-1 btn-ra2-hover">
              新規
            </AppButton>
          </Link>
        </div>
      }
      table={
        <LatlonCachesTableSection
          rows={rows}
          loading={isLoading || isFetching}
          meta={meta}
          defaultPerPage={DEFAULT_PER_PAGE}
          canUseSiteTypeFeatures={showSiteFilter}
          tenantNameBySiteType={tenantNameBySiteType}
          sortInfo={{ columnKey: sortBy, order: order ?? undefined }}
          onSort={handleSort}
          onChangePage={handleChangePage}
        />
      }
    />
  );
};

export default SiteAdminLatlonCachesListPage;
